import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import ShareButton from "@/components/base/ShareButton";
import WorkCard from "@/components/feature/WorkCard";
import EmptyState from "@/components/base/EmptyState";
import useSeo from "@/hooks/useSeo";
import { allActors, formatDate, getNewsByIds, getRelatedWorks, getWorkById } from "@/lib/content";

export default function WorkDetail() {
  const { id = "" } = useParams();
  const work = getWorkById(id);

  useSeo({
    title: work ? `${work.title} - 详细介绍 | 筱田优官网` : "作品详情 | 筱田优官网",
    description: work
      ? `《${work.title}》是${work.year}年上映的${work.type}，由${work.director}执导，${work.cast.join("、")}主演。${work.synopsis}`
      : "筱田优影视作品详情。",
    keywords: work ? `${work.title},${work.cast.join(",")},筱田优作品` : undefined,
  });

  if (!work) {
    return (
      <div className="w-full pt-24 pb-16 px-4 md:px-8">
        <EmptyState
          icon="ri-film-line"
          title="暂无详情内容"
          description="该作品可能已被移除或链接有误，建议返回作品列表查看其他内容。"
          actionTo="/works"
          actionLabel="返回作品列表"
        />
      </div>
    );
  }

  const relatedNews = getNewsByIds(work.relatedNewsIds);
  const recommended = getRelatedWorks(work, 4);

  return (
    <div className="w-full pt-20 md:pt-24">
      <div className="w-full px-4 md:px-8 py-8 md:py-10">
        <Breadcrumb items={[{ label: "作品", to: "/works" }, { label: work.title }]} />
      </div>

      <section className="w-full px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
          <div className="lg:col-span-1">
            <div className="w-full max-w-xs mx-auto lg:mx-0 lg:max-w-none aspect-[2/3] rounded-lg overflow-hidden bg-background-200">
              <img
                src={work.cover}
                alt={`《${work.title}》海报`}
                title={`${work.title} - 筱田优影视作品`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="px-3 py-1 rounded-md bg-primary-100 text-primary-700 text-xs font-medium">
                {work.type}
              </span>
              <span className="px-3 py-1 rounded-md bg-secondary-100 text-secondary-800 text-xs font-medium">
                {work.year}
              </span>
              {work.episodes && (
                <span className="px-3 py-1 rounded-md bg-secondary-100 text-secondary-800 text-xs font-medium">
                  共 {work.episodes} 集
                </span>
              )}
              <span className="px-3 py-1 rounded-md bg-accent-500 text-background-50 text-xs font-bold flex items-center gap-1">
                <i className="ri-star-fill" />
                {work.rating.toFixed(1)}
              </span>
            </div>

            <h1 className="font-heading text-3xl md:text-5xl font-black text-foreground-950 mt-4">
              {work.title}
            </h1>

            <div className="flex flex-wrap gap-2 mt-4">
              {work.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/works?tag=${encodeURIComponent(tag)}`}
                  className="px-3 py-1 rounded-md border border-background-300 text-foreground-600 text-xs hover:border-primary-400 hover:text-primary-600 transition-colors"
                >
                  {tag}
                </Link>
              ))}
            </div>

            <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <p className="text-xs text-foreground-400 mb-1.5">导演</p>
                <p className="text-sm font-medium text-foreground-900">{work.director}</p>
              </div>
              <div>
                <p className="text-xs text-foreground-400 mb-1.5">上映年份</p>
                <p className="text-sm font-medium text-foreground-900">{work.year} 年</p>
              </div>
              <div className="sm:col-span-2">
                <p className="text-xs text-foreground-400 mb-2">主演阵容</p>
                <div className="flex flex-wrap gap-2">
                  {work.cast.map((name) => {
                    const actor = allActors.find((a) => a.name === name);
                    return actor ? (
                      <Link
                        key={name}
                        to={`/actors/${actor.id}`}
                        className="px-3 py-1.5 rounded-full bg-background-100 text-foreground-800 text-sm hover:bg-primary-100 hover:text-primary-700 transition-colors"
                      >
                        {name}
                      </Link>
                    ) : (
                      <span
                        key={name}
                        className="px-3 py-1.5 rounded-full bg-background-100 text-foreground-800 text-sm"
                      >
                        {name}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="mt-7">
              <h2 className="font-heading text-lg font-bold text-foreground-950 mb-3">剧情简介</h2>
              <p className="text-base text-foreground-700 leading-loose">{work.synopsis}</p>
            </div>

            <div className="mt-7 flex flex-wrap items-center gap-3">
              <ShareButton title={work.title} />
              <Link
                to="/works"
                className="px-4 h-10 rounded-md border border-background-300 text-foreground-700 text-sm font-medium hover:border-primary-400 hover:text-primary-600 transition-colors whitespace-nowrap flex items-center gap-2"
              >
                <i className="ri-arrow-left-line" />
                返回作品列表
              </Link>
            </div>
          </div>
        </div>
      </section>

      {relatedNews.length > 0 && (
        <section className="w-full px-4 md:px-8 mt-16">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 flex items-center gap-3 mb-6">
            <span className="w-1.5 h-6 rounded-full bg-accent-500" />
            相关动态
          </h2>
          <div className="flex flex-col gap-4">
            {relatedNews.map((news) => (
              <Link
                key={news.id}
                to={`/news/${news.id}`}
                className="group flex flex-col sm:flex-row gap-4 p-4 rounded-lg border border-background-200 hover:border-primary-300 transition-colors bg-background-50"
              >
                <div className="w-full sm:w-48 h-40 sm:h-28 shrink-0 rounded-md overflow-hidden bg-background-200">
                  <img
                    src={news.cover}
                    alt={`${news.title}配图`}
                    title={`${news.title} - 筱田优影视资讯`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="min-w-0 flex flex-col justify-center">
                  <h3 className="text-base font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {news.title}
                  </h3>
                  <p className="text-sm text-foreground-500 mt-2 line-clamp-2">{news.excerpt}</p>
                  <span className="text-xs text-foreground-400 mt-2">{formatDate(news.date)}</span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {recommended.length > 0 && (
        <section className="w-full px-4 md:px-8 mt-16">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 flex items-center gap-3 mb-6">
            <span className="w-1.5 h-6 rounded-full bg-primary-500" />
            相关作品推荐
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {recommended.map((item) => (
              <WorkCard key={item.id} work={item} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}